import React, { useState } from 'react';
import { PencilIcon } from '@heroicons/react/24/outline';
import { useCollaboratorAllocationsQuery } from '@/features/Collaborators/Allocations/data';
import type { Movimentacao } from '../../../models';
import { useMovimentacaoMutations } from '../../../hooks/data';

interface AlocacaoMovimentacaoLinhaProps {
  solicitacaoId: string;
  movimentacao: Movimentacao;
  colaboradorId: string | null;
  podeEditar: boolean;
}

/**
 * Responsável desta Movimentação (pela alocação dela) — ele vê só esta Movimentação, pela
 * "Minha Equipe"; quem abriu (e o time do DP), o colaborador e o SI veem todas (firestore.rules). O SI troca a
 * alocação aqui; o nome/e-mail do responsável é resolvido pela function a partir da alocação.
 */
export default function AlocacaoMovimentacaoLinha({
  solicitacaoId,
  movimentacao,
  colaboradorId,
  podeEditar,
}: AlocacaoMovimentacaoLinhaProps) {
  const { atualizarMovimentacao } = useMovimentacaoMutations(solicitacaoId);
  const { data: alocacoes = [] } = useCollaboratorAllocationsQuery(podeEditar ? (colaboradorId ?? undefined) : undefined);
  const [editando, setEditando] = useState(false);
  const [alocacaoIdInput, setAlocacaoIdInput] = useState(movimentacao.alocacaoReferenciadaId ?? '');

  const sincronizando =
    Boolean(movimentacao.alocacaoReferenciadaId) && movimentacao.alocacaoResponsavelEmail === undefined;
  const texto = !movimentacao.alocacaoReferenciadaId
    ? 'sem alocação — nenhum gestor vê (só SI, quem abriu e o colaborador)'
    : sincronizando
      ? 'atualizando...'
      : (movimentacao.alocacaoResponsavelNome ?? 'alocação sem responsável definido');

  const salvar = () => {
    atualizarMovimentacao.mutate({
      id: movimentacao.id,
      alocacaoReferenciadaId: alocacaoIdInput || null,
      // Mesmo id de antes = "ressincronizar" (o responsável mudou em Alocações).
      alocacaoPrecisaSincronizar: true,
    });
    setEditando(false);
  };

  if (editando) {
    return (
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <label className="sr-only" htmlFor={`alocacao-${movimentacao.id}`}>
          Alocação desta Movimentação
        </label>
        <select
          id={`alocacao-${movimentacao.id}`}
          className="input-gaveta max-w-md py-2 text-sm"
          value={alocacaoIdInput}
          onChange={(event) => setAlocacaoIdInput(event.target.value)}
        >
          <option value="">Sem alocação (nenhum gestor vê)</option>
          {alocacoes.map((alocacao) => (
            <option key={alocacao.id} value={alocacao.id}>
              {[alocacao.clientName ?? 'Projeto', alocacao.responsibleName ?? 'sem responsável', alocacao.status]
                .filter(Boolean)
                .join(' · ')}
            </option>
          ))}
        </select>
        <button type="button" className="btn-secondary text-sm" onClick={salvar}>
          Salvar
        </button>
        <button type="button" className="text-sm text-gray-500 underline" onClick={() => setEditando(false)}>
          Cancelar
        </button>
      </div>
    );
  }

  return (
    <p className="flex items-center gap-1.5 text-xs text-gray-500">
      <span>
        Responsável: <span className="text-gray-700">{texto}</span>
      </span>
      {podeEditar && colaboradorId && (
        <button
          type="button"
          className="inline-flex h-6 w-6 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          onClick={() => {
            setAlocacaoIdInput(movimentacao.alocacaoReferenciadaId ?? '');
            setEditando(true);
          }}
          title="Trocar ou ressincronizar a alocação desta Movimentação"
        >
          <PencilIcon className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      )}
    </p>
  );
}
